/*───────────────────────────────────────────────────────────────
 📄 File: components/CdrFilterGeneralBox.tsx
 🧩 Description:
    General settings FormBox for CDR Filter: Description,
    Duration (From/To), and TalkTime (From/To) inputs.
────────────────────────────────────────────────────────────────*/

import { Filter } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../hooks/useI18n';
import { DurationRange, TalkTimeRange } from '../types';

interface Props {
    description: string;
    duration: DurationRange;
    talkTime: TalkTimeRange;
    errors: Record<string, string>;
    onDescriptionChange: (v: string) => void;
    onDurationChange: (patch: Partial<DurationRange>) => void;
    onTalkTimeChange: (patch: Partial<TalkTimeRange>) => void;
}

export function CdrFilterGeneralBox({
    description, duration, talkTime, errors,
    onDescriptionChange, onDurationChange, onTalkTimeChange,
}: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('cdrFilters.sections.general')} icon={<Filter className="h-5 w-5" />}>
            <div className="grid grid-cols-8 items-center gap-x-6 gap-y-4">
                {/* Description */}
                <SimpleTooltip content={t('cdrFilters.fields.description.tooltip')} className="col-span-2">
                    <Label htmlFor="description" className="text-left">
                        {t('cdrFilters.fields.description.label')} <span className="text-red-500">*</span>
                    </Label>
                </SimpleTooltip>
                <div className="col-span-5">
                    <Input
                        id="description"
                        value={description}
                        onChange={(e) => onDescriptionChange(e.target.value)}
                        placeholder={t('cdrFilters.fields.description.placeholder')}
                        className={errors.description ? 'border-red-500' : ''}
                    />
                    {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
                </div>
                <div className="col-span-1" />

                {/* From/To column headers */}
                <div className="col-span-2" />
                <Label className="text-xs text-muted-foreground text-center">{t('cdrFilters.fields.duration.from')}</Label>
                <Label className="text-xs text-muted-foreground text-center">{t('cdrFilters.fields.duration.to')}</Label>
                <div className="col-span-1" />
                <Label className="text-xs text-muted-foreground text-center">{t('cdrFilters.fields.talkTime.from')}</Label>
                <Label className="text-xs text-muted-foreground text-center">{t('cdrFilters.fields.talkTime.to')}</Label>
                <div className="col-span-1" />

                {/* Duration row */}
                <SimpleTooltip content={t('cdrFilters.fields.duration.tooltip')} className="col-span-2">
                    <Label htmlFor="duration" className="text-left">{t('cdrFilters.fields.duration.label')}</Label>
                </SimpleTooltip>
                <Input
                    id="durationFrom"
                    type="number"
                    value={duration.from ?? ''}
                    onChange={(e) => onDurationChange({ from: e.target.value ? Number(e.target.value) : null })}
                    placeholder="0"
                />
                <Input
                    id="durationTo"
                    type="number"
                    value={duration.to ?? ''}
                    onChange={(e) => onDurationChange({ to: e.target.value ? Number(e.target.value) : null })}
                    placeholder="0"
                />

                {/* TalkTime row */}
                <SimpleTooltip content={t('cdrFilters.fields.talkTime.tooltip')} className="col-span-1">
                    <Label htmlFor="talkTime" className="text-left">{t('cdrFilters.fields.talkTime.label')}</Label>
                </SimpleTooltip>
                <Input
                    id="talkTimeFrom"
                    type="number"
                    value={talkTime.from ?? ''}
                    onChange={(e) => onTalkTimeChange({ from: e.target.value ? Number(e.target.value) : null })}
                    placeholder="0"
                />
                <Input
                    id="talkTimeTo"
                    type="number"
                    value={talkTime.to ?? ''}
                    onChange={(e) => onTalkTimeChange({ to: e.target.value ? Number(e.target.value) : null })}
                    placeholder="0"
                />
                <div className="col-span-1" />
            </div>
        </FormBox>
    );
}
